import { useMemo } from 'react';
import { first, sortBy } from 'lodash-es';
import { notNil, NonEmptyArray, notEmpty } from '../../../../utils/typescriptUtils';
import { useSearchParamHelpers } from '../../../_shared/react-hooks';
import { IPdpData, TUserSelections } from '../../types/PDP';
import { getProductImageParts, computeSetPreviewImages } from '../../utils';
import { computeModalPicker } from '../ModelChanger/helpers';
import { getProductsDataByColorSelection } from '../product-utils';
import { UGCImageMappings } from './ugcImageMappings';
import { E_GIFT_CARD_PRODUCT_TYPE } from 'src/scripts/utils/constants';

const CurvedSizeCodes = ['2', 's3', 's4', 's5']; // ? [REVISIT] the codes w/ merch

export const getCurveImages = (images?: string[]) => {
  const curveImages = images
    ? images.filter((item) => {
        const sizeCode = getProductImageParts(item)?.sizeCode;
        return sizeCode && CurvedSizeCodes.includes(sizeCode);
      })
    : [];
  return { curveImages, curveIndicators: CurvedSizeCodes };
};
const computePreviewImages = (pdpData: IPdpData, userSelections: TUserSelections): string[] =>
  pdpData.isASet
    ? computeSetPreviewImages(pdpData, userSelections)
    : first(getProductsDataByColorSelection(pdpData, userSelections))?.images ?? [];
export const getVideosByColor = (userSelections: TUserSelections, pdpData: IPdpData) =>
  getProductsDataByColorSelection(pdpData, userSelections)[0]?.videos ?? [];

export const useComputePreviewImages = (
  pdpData: IPdpData,
  userSelections: TUserSelections,
  shouldSuppressModelChanger: boolean,
  shouldShowUGCImageInPdp: boolean
) => {
  // ? [REVISIT] capture from history.location.state? what happens when it gets erased? `InitialUrlSearchParams`
  const urlParams = new URLSearchParams(window.location.search);
  const preferCurveImages = urlParams.get('curve') === '1';

  // ? [REVISIT] merging the "ModelPicker" logic with `computePreviewImages`, which is now a misnomer
  // ? - `getProductImageParts` is called too many times on the same dataset
  const matchingImages: NonEmptyArray<string> = useMemo(() => {
    const images = computePreviewImages(pdpData, userSelections);
    return images as NonEmptyArray<string>;
  }, [pdpData, userSelections]);

  const userSelectedMatchingImages = useMemo(() => {
    const withMetaData = matchingImages.map((image) => getProductImageParts(image)).filter(notNil);

    // pre-sorted by curve preference
    return preferCurveImages ? sortBy(withMetaData, (item) => !CurvedSizeCodes.includes(item.sizeCode!)) : withMetaData;
  }, [matchingImages, preferCurveImages]);

  const { searchParams } = useSearchParamHelpers(undefined, { replace: true });
  const preferredModelCode = searchParams.get('modelCode');

  // ? [REVISIT] figure out how to extract modelPicker
  const modelPicker = useMemo(
    () =>
      computeModalPicker({
        preferCurveImages,
        preferredModelCode,
        userSelectedMatchingImages,
        shouldSuppressModelChanger,
      }),
    [preferCurveImages, preferredModelCode, userSelectedMatchingImages, shouldSuppressModelChanger]
  );

  const productId = getProductsDataByColorSelection(pdpData, userSelections)[0]?.id;
  const UGCImage = shouldShowUGCImageInPdp && productId && UGCImageMappings[productId];
  const isEGiftCard = pdpData.products[0].productType.toLowerCase() === E_GIFT_CARD_PRODUCT_TYPE;
  const previewImagesWithFallbacks: NonEmptyArray<string> = useMemo(() => {
    const NUMBER_OF_FALLBACK_IMAGES_TO_SHOW = 6;

    const computedImagesUrls = (modelPicker?.previewImages || userSelectedMatchingImages).map(({ url }) => url);
    const fallbackImages = pdpData.assets.images.slice(0, NUMBER_OF_FALLBACK_IMAGES_TO_SHOW) as NonEmptyArray<string>;
    if (UGCImage && computedImagesUrls.length > 0) {
      computedImagesUrls[computedImagesUrls.length - 1] = UGCImage;
    }
    return notEmpty(computedImagesUrls) ? computedImagesUrls : fallbackImages;
  }, [modelPicker?.previewImages, pdpData.assets.images, userSelectedMatchingImages, UGCImage]);

  const previewImagesToShow = isEGiftCard ? matchingImages : previewImagesWithFallbacks;
  return { previewImagesToShow, modelPicker };
};

export type UseComputePreviewImagesType = ReturnType<typeof useComputePreviewImages>;
